import { queryOptions, useMutation } from '@tanstack/react-query'

import { SendAuthorizedInlineSurveyFeedbackRequest } from '@forto-dev/notification-center-models'

import { ApiConfig, clientFactory, EndpointConfig } from '@/api/clientFactory'
import { FALLBACK_LANGUAGE } from '@/utils/localisation'
import { error, info } from '@/utils/logger'

import {
  APINotificationPreview,
  APINotificationResponse,
  APINotificationsCount,
  NotificationCount,
  NotificationCountSchema,
  NotificationPreview,
  NotificationPreviewSchema,
  NotificationsResponse,
  NotificationsResponseSchema,
  NotificationStatus,
  NotificationType,
} from '../domain/notification'

const basePath = '/api/notification-center'

export type NotificationSortOrder = 'createdAt' | 'typePriority'

export type NotificationsPageParams = {
  language?: string
  orderBy?: NotificationSortOrder
  orderByDesc?: string
  page: number
  pageSize: number
  type?: NotificationType[]
  status?: NotificationStatus[]
  startDate?: string
  endDate?: string
}

export type UpdateNotificationStatusParams = {
  data: {
    ids: string[]
    status: NotificationStatus
  }
}

const notificationsAPIConfig: ApiConfig<{
  getNotifications: EndpointConfig<
    { pageParam: NotificationsPageParams },
    APINotificationResponse,
    NotificationsResponse
  >
  getNotificationPreview: EndpointConfig<
    { notificationId: string; language: string },
    APINotificationPreview,
    NotificationPreview
  >
  // React Query passes its context to api calls, that's why the first parameter is "unknown" even though the parameters passed from API call are empty.
  getNotificationsCount: EndpointConfig<unknown, APINotificationsCount, NotificationCount>
  updateNotificationStatus: EndpointConfig<UpdateNotificationStatusParams, void>
  submitSurveyStep: EndpointConfig<SendAuthorizedInlineSurveyFeedbackRequest, void>
}> = {
  getNotifications: {
    requestConfig: ({ pageParam }) => ({
      endpoint: `${basePath}/user-notifications`,
      method: 'get',
      deserializationStrategy: NotificationsResponseSchema,
      options: {
        params: Object.fromEntries(Object.entries(pageParam).filter(([, value]) => typeof value !== 'undefined')),
      },
    }),
  },
  getNotificationPreview: {
    requestConfig: ({ notificationId, language }) => ({
      endpoint: `${basePath}/user-notifications/preview/${notificationId}/${language}`,
      method: 'get',
      deserializationStrategy: NotificationPreviewSchema,
    }),
  },
  getNotificationsCount: {
    requestConfig: () => ({
      endpoint: `${basePath}/user-notifications/count`,
      method: 'get',
      deserializationStrategy: NotificationCountSchema,
    }),
  },
  updateNotificationStatus: {
    requestConfig: ({ data }) => ({
      endpoint: `${basePath}/user-notifications/update-status`,
      method: 'post',
      body: data,
    }),
  },
  submitSurveyStep: {
    requestConfig: (props) => ({
      endpoint: `${basePath}/v2/surveys/submit`,
      method: 'put',
      body: props,
    }),
  },
}

export const notificationsClient = clientFactory(notificationsAPIConfig)

export const notificationPreviewQueryOptions = (notificationId: string, language = FALLBACK_LANGUAGE) =>
  queryOptions({
    queryKey: ['userNotificationPreview', notificationId, language],
    queryFn: () => notificationsClient.getNotificationPreview({ notificationId, language }),
  })

export const notificationsCountQueryOptions = () =>
  queryOptions({
    queryKey: ['userNotificationsCount'],
    queryFn: notificationsClient.getNotificationsCount,
  })

export const useSubmitSurveyStep = () => {
  return useMutation({
    mutationFn: notificationsClient.submitSurveyStep,
    onSuccess: async (_data) => {
      info('Survey step submit succeeded')
    },
    onError: (err, _body) => {
      error(`Survey step submit Failed: ${err?.message}`)
    },
  })
}
